import { create } from 'zustand';

/**
 * SourceRecord — runtime mirror of the `$defs/source` shape in
 * `.planning/sources/sources.schema.json`.
 *
 * Fields are permissive (string-typed enums, optional archive/keywords) so
 * that the runtime loader does not Zod-validate sources.json at fetch time —
 * the JSON is build-time validated by `app/tools/sources-schema.test.mjs`
 * (ajv) + `app/tools/build-manifest.mjs` (cross-references every sourceId
 * mentioned in world.json). Keeping the runtime shape wide avoids shipping
 * a ~60 KB ajv bundle to the browser for data that is already vetted at
 * build time.
 */
export type SourceRecord = {
  id: string;
  type: string;
  title: string;
  date: string;
  date_precision?: string;
  attribution: string;
  archive?: string;
  rights: {
    statement_uri: string;
    statement_label: string;
    notes?: string;
    last_audited_on?: string;
  };
  urls: {
    live: string;
    wayback?: string;
    local?: string;
    iiif?: string;
    perma_cc?: string;
  };
  confidence_tier?: string;
  relates_to?: string[];
  keywords?: string[];
  how_we_know?: string;
  ingested_on: string;
  ingested_by: string;
};

interface SourcesStore {
  /** Map of source records keyed by id. Empty until `useSources` populates it. */
  records: Map<string, SourceRecord>;
  /** True once sources.json has successfully fetched + parsed. */
  loaded: boolean;
  /** Populate the store from a flat array of records (idempotent). */
  setRecords: (records: SourceRecord[]) => void;
}

/**
 * Zustand store holding the runtime source library.
 *
 * Plan 04-03 Task 1. Single-flight loading is guaranteed by `useSources`
 * (the only writer): it fires exactly once on app mount (mounted at App
 * root in Task 4) and skips the fetch if `loaded` is already true.
 * Components read via `useSourcesStore((s) => s.records)` or the imperative
 * `getRecord(id)` helper below.
 */
export const useSourcesStore = create<SourcesStore>((set) => ({
  records: new Map(),
  loaded: false,
  setRecords: (records) => {
    const map = new Map<string, SourceRecord>();
    for (const r of records) map.set(r.id, r);
    set({ records: map, loaded: true });
  },
}));

/**
 * Imperative helper for non-React callers (e.g. selection handlers) or for
 * unit tests that want to assert on loaded data without re-rendering.
 */
export function getRecord(id: string): SourceRecord | undefined {
  return useSourcesStore.getState().records.get(id);
}
